/* The staged Atlas bundle, from the Deck's side.
 *
 * ── Why the client reads a manifest instead of hardcoding paths ─────────────
 *
 * A bundle records which layers it actually contains, at which zooms, under which licence. If the
 * Deck hardcoded `/bundle/eox-terrain-light/{z}/{x}/{y}.jpg` then a bundle staged with different
 * layers — a different theatre, a customer's own imagery — would need a client release to be
 * usable, and a bundle staged WITHOUT that layer would render as a grey void with no explanation.
 *
 * ── CON-01 ─────────────────────────────────────────────────────────────────
 *
 * Every URL here is same-origin and relative to the page the operator already loaded. There is no
 * host to configure, which is the point: a client with no configurable upstream cannot be
 * misconfigured into having one.
 *
 * ── Absent is a normal state ───────────────────────────────────────────────
 *
 * A deployment with no staged bundle is not broken. It falls back to the synthetic offline style —
 * inline coastline geometry, no tiles at all — and the mode badge says so. What must never happen
 * is falling back to the NETWORK, which is the failure this whole subsystem exists to prevent.
 */

export interface BundleLayer {
  source: string
  name: string
  kind: 'raster' | 'dem' | 'vector' | string
  /** Present only for staged layers. Relative to the bundle root. */
  urlTemplate?: string
  extension?: string
  licence: string
  attribution: string
  requiresLegalReview?: boolean
  zoomMin?: number
  zoomMax?: number
  tiles?: number
  bytes?: number
  /** `false` for a layer the stager deliberately did not produce, with a reason. */
  staged?: boolean
  reason?: string
}

export interface BundleManifest {
  name: string
  digest: string
  aoi: { west: number; south: number; east: number; north: number }
  zoomMin: number
  zoomMax: number
  layers: Record<string, BundleLayer>
  createdIso: string
  files?: unknown[]
}

/** Where the bundle is served. Same-origin, absolute, and not configurable — see the header. */
export const BUNDLE_ROOT = '/bundle'

let cached: BundleManifest | null = null
let attempted = false

/** The manifest, or null when this deployment has no staged bundle. */
export function bundleManifest(): BundleManifest | null {
  return cached
}

export function bundleAttempted(): boolean {
  return attempted
}

/**
 * Fetch the manifest once. Safe to call repeatedly; the second call is free.
 *
 * A 404 is a normal answer — it means nothing has been staged here — so it resolves to null
 * rather than throwing. An error would put a red banner on a deployment that is working exactly
 * as configured.
 */
export async function loadBundleManifest(): Promise<BundleManifest | null> {
  if (attempted) return cached
  attempted = true
  try {
    const res = await fetch(`${BUNDLE_ROOT}/MANIFEST.json`, { cache: 'no-store' })
    if (!res.ok) return null
    const manifest = (await res.json()) as BundleManifest
    // A manifest with no layers is a staging failure wearing a success's clothes: the file is
    // there, so nothing 404s, and every tile request quietly returns nothing.
    if (!manifest || typeof manifest !== 'object' || !manifest.layers) return null
    cached = manifest
    return cached
  } catch {
    return null
  }
}

/** The layers that actually have tiles, in a stable order. */
export function stagedLayers(manifest: BundleManifest | null): BundleLayer[] {
  if (!manifest) return []
  return Object.values(manifest.layers)
    .filter((l) => l.staged !== false && !!l.urlTemplate)
    .sort((a, b) => a.source.localeCompare(b.source))
}

export function findLayer(manifest: BundleManifest | null, kind: 'raster' | 'dem'): BundleLayer | null {
  return stagedLayers(manifest).find((l) => l.kind === kind) ?? null
}

/** Which staged layer backs a given basemap choice.
 *
 * The Deck's basemap ids are product vocabulary ("Satellite", "Terrain") and a bundle's layer ids
 * are source vocabulary ("eox-s2cloudless"). Mapping them here keeps the menu meaningful in bundle
 * mode: a basemap the bundle did not stage is marked unavailable rather than silently rendering
 * some OTHER layer under its name, which would be a map lying about what it is showing.
 */
const BASEMAP_LAYERS: Record<string, string> = {
  satellite: 'eox-s2cloudless',
  'terrain-light': 'eox-terrain-light',
  // Vector, and not stageable by the current tool — see orbis_atlas/stage.py.
  tactical: 'openfreemap-vector',
}

export function layerForBasemap(
  manifest: BundleManifest | null,
  basemapId: string,
): BundleLayer | null {
  const wanted = BASEMAP_LAYERS[basemapId]
  if (!wanted) return null
  return stagedLayers(manifest).find((l) => l.source === wanted) ?? null
}

/** Basemap ids this bundle can actually draw, in the Deck's own order. */
export function availableBasemaps(manifest: BundleManifest | null): string[] {
  return Object.keys(BASEMAP_LAYERS).filter((id) => layerForBasemap(manifest, id) !== null)
}

/** Absolute, same-origin tile URL template for MapLibre. */
export function tileUrl(layer: BundleLayer): string {
  return `${BUNDLE_ROOT}/${layer.urlTemplate}`
}

/** The attribution line for everything staged — assembled from the bundle, never hardcoded. */
export function bundleAttribution(manifest: BundleManifest | null): string {
  const parts = stagedLayers(manifest).map((l) => l.attribution)
  return parts.length ? `${parts.join(' · ')} — staged bundle` : 'No staged bundle'
}
